import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { Target, Shield, Activity, Droplet, Wind, Package, ArrowRight, CheckCircle, Star } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const StoreHome = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const { toast } = useToast();
  const { addToCart, getCartItemCount } = useCart();
  const { products } = useProducts();
  const featuredProduct = products.find(p => p.id === 'vesta-apex-suit-001');

  const handleNewsletterSignup = (e) => {
    e.preventDefault();
    toast({
      title: "You're subscribed!",
      description: "We'll keep you updated on new drops and limited editions.",
    });
    setNewsletterEmail('');
  };

  const keyBenefits = [
    {
      icon: Target,
      title: "Women's-Specific Fit",
      description: "No more oversized suits that drag through paint"
    },
    {
      icon: Shield,
      title: "Static-Dissipative",
      description: "Paint-safe, booth-approved materials"
    },
    {
      icon: Activity,
      title: "Full Range of Motion",
      description: "Squat, bend, and reach without pulling or splitting seams"
    },
    {
      icon: Package,
      title: "Side Zipper Access",
      description: "Access clothing underneath without removing the suit"
    }
  ];

  const handleAddToCart = (product) => {
    addToCart(product, product.sizes[0] || 'One Size', product.colors?.[0] || null, 1);
    toast({
      title: "Added to cart!",
      description: `${product.name} has been added to your cart.`,
    });
  };

  const pressOutlets = [
    'BodyShop News',
    'Paint & Panel',
    'Auto Women',
    'Motor Trader',
    'Townsville Bulletin'
  ];

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#004D51] via-[#003338] to-[#001f22]"></div>
          <div className="absolute inset-0" style={{
            backgroundImage: 'url(https://cdn.shopify.com/s/files/1/0770/5572/1703/files/160126_VestaWorkwear-19.jpg?v=1772281308)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15
          }}></div>
          <div className="absolute inset-0 opacity-5" style={{
            backgroundImage: 'linear-gradient(#F39C49 1px, transparent 1px), linear-gradient(90deg, #F39C49 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="container mx-auto px-6 z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div className="text-white space-y-8">
              <div className="inline-block mb-4">
                <div className="px-6 py-2 bg-[#DB5A14] text-white font-black text-sm tracking-widest transform -rotate-1">
                  NOW AVAILABLE // PATENT PENDING
                </div>
              </div>

              <h1 className="text-5xl md:text-7xl font-black leading-tight">
                <span className="block">POWER.</span>
                <span className="block text-[#F39C49]">PRECISION.</span>
                <span className="block">PROTECTION.</span>
              </h1>

              <p className="text-xl md:text-2xl text-[#CFD2D4] font-medium leading-relaxed">
                Professional refinishing suits engineered for women who work in the spray booth, not just walk through it.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Link to="/shop">
                  <Button className="h-16 px-10 bg-[#DB5A14] hover:bg-[#F39C49] text-white font-black text-lg rounded-lg transition-all transform hover:scale-105">
                    Shop the Suit
                  </Button>
                </Link>
                <Link to="/about-vesta">
                  <Button className="h-16 px-10 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white font-black text-lg rounded-lg transition-all border-2 border-white/30">
                    Why Vesta?
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Product Image */}
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-full h-full bg-[#F39C49] rounded-2xl opacity-20 transform rotate-3"></div>
              <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border-4 border-[#F39C49]">
                <img
                  src={featuredProduct.images[0]}
                  alt="Vesta Apex Spray Suit"
                  className="w-full rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits Strip */}
      <section className="py-16 bg-[#F3CEB2]/30">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {keyBenefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#004D51] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-black text-[#004D51] mb-2">{benefit.title}</h3>
                <p className="text-sm text-[#545759]">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* As Seen In - Press Strip */}
      <section className="py-12 bg-white border-b border-gray-200" data-testid="as-seen-in-strip">
        <div className="container mx-auto px-6">
          <p className="text-center text-xs font-black uppercase tracking-[0.25em] text-[#545759] mb-8">
            As Seen In
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6 md:gap-x-16">
            {pressOutlets.map((outlet, index) => (
              <Link
                key={outlet}
                to="/press"
                data-testid={`as-seen-in-${index}`}
                className="text-base md:text-lg font-black uppercase tracking-wide text-[#545759]/50 hover:text-[#004D51] transition-colors"
              >
                {outlet}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hero Product Highlight */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-5xl md:text-6xl font-black text-[#004D51] mb-4">
                Meet the <span className="text-[#DB5A14]">Vesta Apex</span>
              </h2>
              <p className="text-xl text-[#545759] max-w-2xl mx-auto">
                The refinishing suit designed specifically for women. No compromise. No excuses.
              </p>
            </div>

            <ProductCard product={featuredProduct} onAddToCart={handleAddToCart} />

            <div className="mt-12 text-center">
              <Link to={`/product/${featuredProduct.slug}`}>
                <Button className="bg-[#004D51] hover:bg-[#003338] text-white font-black px-8 py-6 rounded-lg text-lg">
                  View Technical Features <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-24 bg-[#004D51] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-black mb-4">The Problem With <span className="text-[#F39C49]">"Unisex"</span></h2>
              <p className="text-xl text-[#CFD2D4]">Let's be real about what's been available</p>
            </div>

            <div className="grid md:grid-cols-2 gap-12">
              {/* Before */}
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 border-2 border-red-500">
                <h3 className="text-2xl font-black text-red-400 mb-6">❌ GENERIC SUITS</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="text-red-400 mr-3 text-xl">×</span>
                    <span>Excess fabric bunching around hips and seat</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-3 text-xl">×</span>
                    <span>Sleeves and legs dragging through fresh paint</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-3 text-xl">×</span>
                    <span>Rarely available in smaller sizes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-3 text-xl">×</span>
                    <span>Restricted movement when bending and reaching</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-400 mr-3 text-xl">×</span>
                    <span>Designed for men's bodies, not yours</span>
                  </li>
                </ul>
              </div>

              {/* After */}
              <div className="bg-[#F39C49] rounded-xl p-8 border-4 border-white">
                <h3 className="text-2xl font-black text-[#004D51] mb-6">✓ VESTA APEX SUIT</h3>
                <ul className="space-y-4 text-[#004D51]">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                    <span className="font-bold">Tailored women's fit with contoured waist and seat</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                    <span className="font-bold">No excess fabric to contaminate your work</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                    <span className="font-bold">Side zippers for easy access to clothing underneath</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                    <span className="font-bold">Full range of motion for all workshop tasks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0" />
                    <span className="font-bold">Engineered for women's proportions</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fit & Sizing Confidence */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl font-black text-[#004D51] mb-6">
              Finally, A Suit That <span className="text-[#DB5A14]">Fits</span>
            </h2>
            <p className="text-xl text-[#545759] mb-8">
              Designed in collaboration with female spray painters and apprentices who know what actually works in the booth.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-[#F3CEB2]/30 rounded-xl p-6">
                <div className="text-3xl font-black text-[#004D51] mb-2">XS - XXL</div>
                <p className="text-[#545759] font-medium">Comprehensive size range</p>
              </div>
              <div className="bg-[#F3CEB2]/30 rounded-xl p-6">
                <div className="text-3xl font-black text-[#004D51] mb-2">🇦🇺</div>
                <p className="text-[#545759] font-medium">Designed in Australia</p>
              </div>
              <div className="bg-[#F3CEB2]/30 rounded-xl p-6">
                <div className="text-3xl font-black text-[#004D51] mb-2">100%</div>
                <p className="text-[#545759] font-medium">Real input from techs</p>
              </div>
            </div>

            <Link to="/fit">
              <Button className="bg-[#004D51] hover:bg-[#003338] text-white font-black px-8 py-6 rounded-lg text-lg">
                View Fit Guide & Size Chart
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Sustainability Teaser */}
      <section className="py-24 bg-[#F3CEB2]/20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-black text-[#004D51] mb-6">
                  Wash & Re-Wear, <span className="text-[#DB5A14]">Not Disposable</span>
                </h2>
                <p className="text-lg text-[#545759] mb-6">
                  Premium materials and construction mean your suit is designed to be washed and worn again, not tossed after minimal use.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#DB5A14] mr-3 flex-shrink-0 mt-1" />
                    <span className="text-[#545759]">Premium weight fabric with reinforced stress points</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#DB5A14] mr-3 flex-shrink-0 mt-1" />
                    <span className="text-[#545759]">Patent pending design for women</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#DB5A14] mr-3 flex-shrink-0 mt-1" />
                    <span className="text-[#545759]">Machine washable - designed for multiple uses</span>
                  </li>
                </ul>
                <Link to="/sustainability">
                  <Button className="bg-[#004D51] hover:bg-[#003338] text-white font-black px-6 py-3 rounded-lg">
                    Learn About Our Materials
                  </Button>
                </Link>
              </div>

              <div className="relative h-96 rounded-2xl overflow-hidden">
                <img
                  src="/automotive-3.jpg"
                  alt="Sustainability"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#004D51] to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-24 bg-gradient-to-br from-[#DB5A14] to-[#F39C49] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-5xl font-black mb-6">Stay in the Loop</h2>
            <p className="text-xl mb-8 text-white/90">
              Be first in line for new drops, limited editions, and trade-only offers.
            </p>

            <form onSubmit={handleNewsletterSignup} className="max-w-md mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  required
                  className="flex-1 h-14 px-6 text-lg bg-white text-[#004D51] border-2 border-white rounded-lg font-medium"
                />
                <Button
                  type="submit"
                  className="h-14 px-8 bg-white text-[#DB5A14] hover:bg-[#F3CEB2] font-black text-lg rounded-lg transition-all"
                >
                  Subscribe
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
};

export default StoreHome;
